import { msalInstance } from "@/config/msalConfig";
import authService from "@/services/authService";
import {
  Button,
  Col,
  Input,
  message,
  Row,
  Card,
  Typography,
  Space,
  Avatar,
} from "antd";
import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  WindowsOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import {
  setMicrosoftSetup,
  setIsSSO,
  setMpin,
  clearMpin,
} from "@/stores/slice/authSlice";
import Logo from "@/assets/logo.jpeg";
import { ChevronRight } from "lucide-react";
import shipService from "@/services/shipService";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

const { Title, Text, Paragraph } = Typography;

const SetupPage = () => {
  const dispatch = useDispatch();
  const userId = localStorage.getItem("user_id");
  const navigate = useNavigate();
  // Get state from Redux
  const { mpin_setup, microsoft_setup } = useSelector((state) => state.auth);

  const [pin, setPin] = useState(["", "", "", "", "", ""]);
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState({ microsoft: false, mpin: false });
  const inputRefs = useRef([]);
  const { role } = useAuth();

  // Initialize component state from Redux
  useEffect(() => {
    // Component state will reflect Redux state automatically through selectors
  }, [mpin_setup, microsoft_setup]);

  const onSignMicrosoft = async () => {
    setLoading((prev) => ({ ...prev, microsoft: true }));

    try {
      dispatch(setIsSSO(true));
      localStorage.setItem("sso_auth", true);

      await msalInstance.initialize();
      const loginResponse = await msalInstance.loginPopup({
        scopes: ["user.read"],
      });
      const accessToken = loginResponse.accessToken;
      const res = await authService.microsoftAuth({
        accessToken,
        user_id: userId,
      });

      if (res.success) {
        message.success("Microsoft authentication configured successfully");
        dispatch(setMicrosoftSetup(true));
      } else {
        message.error("Failed to configure Microsoft authentication");
        dispatch(setMicrosoftSetup(false));
      }
    } catch (error) {
      message.error("Microsoft authentication failed");
      dispatch(setMicrosoftSetup(false));
      console.error("Microsoft auth error:", error);
    } finally {
      setLoading((prev) => ({ ...prev, microsoft: false }));
    }
  };

  const handleInputChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSetupMpin = async () => {
    const mpinValue = pin.join("");

    if (mpinValue.length !== 6) {
      message.warning("Please enter a complete 6-digit MPIN");
      return;
    }

    setLoading((prev) => ({ ...prev, mpin: true }));

    try {
      dispatch(setMpin(mpinValue));
      message.success("MPIN configured successfully");

      setPin(["", "", "", "", "", ""]);
    } catch (error) {
      message.error("Failed to configure MPIN");
      console.error("MPIN setup error:", error);
    } finally {
      setLoading((prev) => ({ ...prev, mpin: false }));
    }
  };

  const resetPin = () => {
    setPin(["", "", "", "", "", ""]);
    inputRefs.current[0]?.focus();
  };

  const handleReconfigureMicrosoft = () => {
    dispatch(setMicrosoftSetup(false));
    dispatch(setIsSSO(false));
    localStorage.removeItem("sso_auth");
  };

  const handleChangeMpin = () => {
    dispatch(clearMpin());
    resetPin();
  };

  const authenticatedRoutes = () => {
    if (role === "MASTER" || role === "CHIEF_ENGINEER") {
      navigate("/admin");
    } else if (role === "ENGINE_ROOM" || role === "BRIDGE") {
      navigate("/user");
    } else if (role === "SUPERADMIN") {
      navigate("/superadmin");
    } else if (
      role === "OWNER" ||
      role === "OWNERGROUP" ||
      role === "FLEET_MANAGER" ||
      role === "VESSEL_MANANGER"
    ) {
      navigate("/management");
    }
  };

  const onNextSetup = async () => {
     await shipService.configureuser({
      reset_new: false,
      user_id: userId,
    });
    authenticatedRoutes();
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundImage: "linear-gradient(315deg, #27a770 0%, #efefef 74%)",
        padding: "40px 20px",
      }}
    >
      <div style={{ maxWidth: "800px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "40px" }}>
          <Avatar style={{ width: "70px", height: "70px" }} src={Logo} />

          <Title
            className="my-2"
            level={1}
            style={{ color: "#27a770", margin: 0 }}
          >
            SeaFlash Setup
          </Title>
          <Paragraph
            className="text-secondary"
            style={{ fontSize: "16px", margin: "10px 0 0 0" }}
          >
            Configure your authentication methods to secure your maritime
            operations
          </Paragraph>
        </div>

        <Row gutter={[24, 24]}>
          {/* Step 1: Microsoft Authentication */}
          <Col xs={24} lg={12}>
            <Card
              style={{
                height: "100%",
                borderRadius: "16px",
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                border: microsoft_setup
                  ? "2px solid #52c41a"
                  : "1px solid #e8e8e8",
              }}
            >
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                {microsoft_setup ? (
                  <CheckCircleOutlined
                    style={{
                      fontSize: "48px",
                      color: "#52c41a",
                      marginBottom: "16px",
                    }}
                  />
                ) : (
                  <WindowsOutlined
                    style={{
                      fontSize: "48px",
                      color: "white",
                      marginBottom: "16px",
                    }}
                  />
                )}

                <Title level={3} style={{ marginBottom: "12px" }}>
                  Microsoft Authentication
                </Title>

                <Paragraph
                  style={{ color: "#666", marginBottom: "24px" }}
                  className="mt-4"
                >
                  Connect your Microsoft account for secure single sign-on
                  access to SeaFlash
                </Paragraph>

                {microsoft_setup ? (
                  <div>
                    <Text
                      type="success"
                      style={{ fontSize: "16px", fontWeight: "500" }}
                    >
                      ✓ Microsoft authentication configured
                    </Text>
                    <br />
                    <Button
                      type="text"
                      onClick={handleReconfigureMicrosoft}
                      style={{ marginTop: "12px" }}
                    >
                      Reconfigure
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="primary"
                    size="large"
                    icon={<WindowsOutlined className="text-white" />}
                    onClick={onSignMicrosoft}
                    loading={loading.microsoft}
                    className="mt-5"
                    style={{
                      height: "48px",
                      borderRadius: "24px",
                      background: "#27a770",
                      borderColor: "#27a770",
                      fontSize: "16px",
                      fontWeight: "500",
                    }}
                  >
                    Connect Microsoft Account
                  </Button>
                )}
              </div>
            </Card>
          </Col>

          {/* Step 2: MPIN Setup */}
          <Col xs={24} lg={12}>
            <Card
              style={{
                height: "100%",
                borderRadius: "16px",
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                border: mpin_setup ? "2px solid #52c41a" : "1px solid #e8e8e8",
              }}
            >
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                {mpin_setup ? (
                  <CheckCircleOutlined
                    style={{
                      fontSize: "48px",
                      color: "#52c41a",
                      marginBottom: "16px",
                    }}
                  />
                ) : (
                  <SafetyOutlined
                    style={{
                      fontSize: "48px",
                      color: "#27a770",
                      marginBottom: "16px",
                    }}
                  />
                )}

                <Title level={3} style={{ marginBottom: "12px" }}>
                  MPIN Setup
                </Title>

                <Paragraph style={{ color: "#666", marginBottom: "24px" }}>
                  Create a 6-digit Mobile PIN for quick and secure access
                </Paragraph>

                {mpin_setup ? (
                  <div>
                    <Text
                      type="success"
                      style={{ fontSize: "16px", fontWeight: "500" }}
                    >
                      ✓ MPIN configured successfully
                    </Text>
                    <br />
                    <Button
                      type="text"
                      onClick={handleChangeMpin}
                      style={{ marginTop: "12px" }}
                    >
                      Change MPIN
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Row
                      gutter={8}
                      justify="center"
                      style={{ marginBottom: "20px" }}
                    >
                      {pin.map((digit, index) => (
                        <Col key={index}>
                          <Input
                            ref={(el) => (inputRefs.current[index] = el)}
                            value={digit}
                            onChange={(e) =>
                              handleInputChange(index, e.target.value)
                            }
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            maxLength={1}
                            style={{
                              width: "48px",
                              height: "48px",
                              textAlign: "center",
                              fontSize: "18px",
                              fontWeight: "bold",
                              borderRadius: "8px",
                              border: digit
                                ? "2px solid #27a770"
                                : "2px solid #d9d9d9",
                            }}
                            type={showPin ? "text" : "password"}
                            inputMode="numeric"
                          />
                        </Col>
                      ))}
                    </Row>

                    <div style={{ marginBottom: "20px" }}>
                      <Button
                        type="text"
                        icon={
                          showPin ? <EyeInvisibleOutlined /> : <EyeOutlined />
                        }
                        onClick={() => setShowPin(!showPin)}
                        style={{ color: "#8c8c8c", fontSize: "14px" }}
                      >
                        {showPin ? "Hide" : "Show"} PIN
                      </Button>
                    </div>

                    <Space>
                      <Button
                        type="primary"
                        size="large"
                        onClick={handleSetupMpin}
                        loading={loading.mpin}
                        disabled={pin.join("").length !== 6}
                        style={{
                          color: "white",
                          height: "48px",
                          borderRadius: "24px",
                          fontSize: "16px",
                          fontWeight: "500",
                          background: "#27a770",
                          borderColor: "#27a770",
                        }}
                      >
                        Create MPIN
                      </Button>
                      <Button
                        size="large"
                        onClick={resetPin}
                        style={{
                          height: "48px",
                          borderRadius: "24px",
                        }}
                      >
                        Reset
                      </Button>
                    </Space>
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Setup Complete Section */}
        {microsoft_setup && mpin_setup && (
          <Card
            style={{
              marginTop: "32px",
              borderRadius: "16px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              background: "linear-gradient(135deg, #52c41a, #73d13d)",
              border: "none",
            }}
          >
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <CheckCircleOutlined
                style={{
                  fontSize: "48px",
                  color: "#fff",
                  marginBottom: "16px",
                }}
              />
              <Title level={2} style={{ color: "#fff", margin: "0 0 12px 0" }}>
                Setup Complete!
              </Title>
              <Paragraph
                style={{
                  color: "rgba(255,255,255,0.9)",
                  fontSize: "16px",
                  margin: 0,
                }}
              >
                Your SeaFlash application is now configured and ready to use
              </Paragraph>
            </div>
          </Card>
        )}

        {/* Footer */}
        <Button
          className="mt-4 float-end"
          type="default"
          icon={<ChevronRight />}
          size="large"
          onClick={onNextSetup}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default SetupPage;
